import { memo, useMemo } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import PropertyCard from "@/components/PropertyCard";
import { useProperties } from "@/hooks/useProperties";
import { formatErrorMessage } from "@/utils/errorUtils";

const FeaturedPropertiesSection = memo(() => {
  const { t } = useLanguage();
  const { data: properties = [], isLoading, error, isError } = useProperties();

  const featuredProperties = useMemo(() => {
    return properties.filter((property) => property.featured);
  }, [properties]);

  const loadingSkeleton = useMemo(
    () => (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="bg-gray-200 rounded-2xl h-96 animate-pulse"
          />
        ))}
      </div>
    ),
    [],
  );

  if (isError && error) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t("featured.title")}
            </h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md mx-auto">
              <p className="text-red-800">
                Error al cargar propiedades: {formatErrorMessage(error)}
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            {t("featured.title")}
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t("featured.subtitle")}
          </p>
        </div>

        {isLoading ? (
          loadingSkeleton
        ) : featuredProperties.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProperties.map((property) => (
              <PropertyCard
                key={property.id}
                id={property.id}
                title={property.title}
                shortDescription={property.shortDescription}
                price={property.price}
                rating={property.rating}
                guests={property.guests}
                images={property.images}
                neighborhood={property.neighborhood}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">
              No hay propiedades destacadas disponibles en este momento.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Total de propiedades cargadas: {properties.length}
            </p>
          </div>
        )}
      </div>
    </section>
  );
});

FeaturedPropertiesSection.displayName = "FeaturedPropertiesSection";

export default FeaturedPropertiesSection;
